import axios, { type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';

import type { ApiEnvelope } from '@/types/api.types';
import type {
  LoginRequest,
  LoginResponse,
  LogoutRequest,
  RefreshResponse,
  SignUpRequest,
  SignUpResponse,
} from '@/types/auth.types';
import type { PublicUser } from '@/types/user.types';
import { env } from '@/configs/env.config';

import { Sentry, isSentryEnabled } from '@/configs/sentry.config';
import { logBreadcrumb } from '@/utils/logger.util';

import { authStorage } from './authStorage.service';

type RetriableConfig = InternalAxiosRequestConfig & { _retry?: boolean };

/** Endpoints that must never trigger the auth-refresh retry (avoids recursion/loops). */
const AUTH_PATHS = ['/auth/login', '/auth/signup', '/auth/refresh', '/auth/logout'];

const client: AxiosInstance = axios.create({
  baseURL: env.VITE_API_BASE_URL,
  timeout: 15_000,
});

client.interceptors.request.use((config) => {
  const token = authStorage.getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // The server adopts this id for every log line it writes for this request, so a Sentry
  // event can be grepped straight out of server/logs/app-*.log.
  config.headers['x-request-id'] =
    typeof crypto?.randomUUID === 'function'
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2);
  // Distinguishes admin-panel traffic from the mobile app's on shared endpoints.
  config.headers['x-client'] = 'web';
  return config;
});

/**
 * Refreshes the access token using the stored refresh token. A bare axios call (not `client`)
 * avoids interceptor recursion. Concurrent 401s share a single in-flight refresh.
 */
let refreshPromise: Promise<string> | null = null;

const runRefresh = async (): Promise<string> => {
  const refreshToken = authStorage.getRefreshToken();
  if (!refreshToken) {
    throw new Error('No refresh token');
  }
  // Bare axios call — bypasses the unwrap interceptor below, so unwrap the envelope by hand.
  const { data: body } = await axios.post<ApiEnvelope<RefreshResponse>>(
    `${env.VITE_API_BASE_URL}/auth/refresh`,
    { refreshToken },
    { timeout: 15_000 },
  );
  const tokens = body.data;
  if (!tokens) {
    throw new Error('Refresh failed');
  }
  authStorage.setTokens({ token: tokens.token, refreshToken: tokens.refreshToken });
  return tokens.token;
};

/**
 * The server wraps every response in `{ status, data, error, message }`. Unwrap once here so
 * callers keep receiving the inner payload via `.then((r) => r.data)`. Registered BEFORE the
 * auth-refresh interceptor so a retried request is only unwrapped a single time.
 */
client.interceptors.response.use((response) => {
  const envelope = response.data as unknown as ApiEnvelope<unknown> | null;
  (response as { data: unknown }).data = envelope?.data ?? null;
  return response;
});

client.interceptors.response.use(
  (response) => response,
  async (error: unknown) => {
    if (!axios.isAxiosError(error) || error.response?.status !== 401) {
      return Promise.reject(error);
    }

    const original = error.config as RetriableConfig | undefined;
    const isAuthCall = AUTH_PATHS.some((path) => original?.url?.includes(path));
    if (!original || original._retry || isAuthCall) {
      authStorage.clear();
      return Promise.reject(error);
    }

    original._retry = true;
    try {
      refreshPromise = refreshPromise ?? runRefresh();
      const newToken = await refreshPromise;
      original.headers.Authorization = `Bearer ${newToken}`;
      return await client(original);
    } catch (refreshError) {
      authStorage.clear();
      return Promise.reject(refreshError);
    } finally {
      refreshPromise = null;
    }
  },
);

/**
 * Records every request failure. Sits between the auth-refresh interceptor (so a 401 that gets
 * silently refreshed is never reported) and the terminal one below (which flattens the
 * AxiosError into a plain Error, losing the status and the request id).
 */
client.interceptors.response.use(
  (response) => response,
  (error: unknown) => {
    if (axios.isAxiosError(error)) {
      const status = error.response?.status;
      const requestId = error.config?.headers?.['x-request-id'];
      const label = `API ${error.config?.method?.toUpperCase() ?? '?'} ${error.config?.url ?? '?'} -> ${status ?? error.code ?? 'network-error'}`;

      logBreadcrumb(label, { requestId: String(requestId ?? 'unknown') });

      // 4xx are the admin's own doing (bad input, expired session) and the UI already shows
      // them. Only server faults are worth an event.
      if (isSentryEnabled() && (status === undefined || status >= 500)) {
        Sentry.captureException(error, {
          tags: { requestId: String(requestId ?? 'unknown') },
        });
      }
    }
    return Promise.reject(error);
  },
);

/**
 * Terminal interceptor: surface the server's structured `error` body as a real `Error` so callers
 * (TanStack Query, forms) get `error.message`. Registered LAST so it runs after the auth-refresh
 * retry has had its chance.
 */
client.interceptors.response.use(
  (response) => response,
  (error: unknown) => {
    if (axios.isAxiosError(error)) {
      const envelope = error.response?.data as ApiEnvelope<unknown> | undefined;
      if (envelope?.error) {
        const apiError = new Error(envelope.error.message);
        Object.assign(apiError, { code: envelope.error.code, details: envelope.error.details });
        return Promise.reject(apiError);
      }
    }
    return Promise.reject(error instanceof Error ? error : new Error('Request failed'));
  },
);

export const apiClient = client;

export const authApi = {
  signUp: (body: SignUpRequest) =>
    client.post<SignUpResponse>('/auth/signup', body).then((r) => r.data),
  login: (body: LoginRequest) =>
    client.post<LoginResponse>('/auth/login', body).then((r) => r.data),
  logout: (body: LogoutRequest) => client.post<void>('/auth/logout', body).then((r) => r.data),
};

export const userApi = {
  getMe: () => client.get<PublicUser>('/users/me').then((r) => r.data),
};
